import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";

/**
 * Checkbox — token-driven Radix checkbox.
 *
 * Three states via Radix's `checked` prop: `false`, `true`, `"indeterminate"`.
 * The indicator switches glyph based on state — `<Minus>` for indeterminate
 * (used for "some items selected" tri-state lists), `<Check>` for true.
 *
 *   <div className="flex items-center gap-2">
 *     <Checkbox id="terms" checked={agreed} onCheckedChange={setAgreed} />
 *     <Label htmlFor="terms">I agree to the terms</Label>
 *   </div>
 *
 * Override the icon per-instance via `icon` if you need a non-Check glyph
 * (rare — most surfaces want the canonical check / minus pair).
 *
 * Visual treatment is calm-by-default to read well in both compact form rows
 * and standalone use:
 *
 * - **Rest border** is `border-input` (neutral) instead of a saturated
 *   primary tint. Hover ramps the border slightly to signal interactivity
 *   without flashing brand color.
 * - **Box** is `h-4 w-4` with `rounded-[5px]` — softer than `rounded-sm` so
 *   the corner profile matches `<Input rounded-lg>`'s feel at a smaller
 *   visual weight.
 * - **Check / Minus glyphs** sit at `h-3 w-3` with `strokeWidth={3}` so the
 *   checked state reads crisply with breathing room around the glyph
 *   instead of filling the box edge to edge.
 * - **Transitions** use `transition-[background-color,border-color,color]
 *   duration-180 ease-smooth-out`, matching `<Input>`'s focus easing
 *   exactly so the form-primitive family reads as one design system.
 *   Indicator glyph fades + zooms in via `data-[state=checked]:animate-in`
 *   after the background settles, layout-first-then-opacity per the
 *   workspace voice rule.
 * - **Focus ring** matches `<Input>`: `focus-visible:ring-2 ring-ring
 *   ring-offset-2`.
 */
export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  /**
   * Replace the checked-state glyph. Default: lucide `Check`. Indeterminate
   * always renders `Minus` regardless of this prop. Type matches Lucide's
   * `LucideIcon` signature so any `lucide-react` icon drops in directly.
   */
  icon?: LucideIcon;
}

export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, icon: Icon = Check, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      "peer h-4 w-4 shrink-0 rounded-[5px] border border-input bg-background ring-offset-background",
      "transition-[background-color,border-color,color] duration-200 ease-smooth-out",
      "hover:border-foreground/40",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-input",
      "data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground",
      "data-[state=indeterminate]:bg-primary data-[state=indeterminate]:border-primary data-[state=indeterminate]:text-primary-foreground",
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn(
        "flex items-center justify-center text-current",
        "data-[state=checked]:animate-in data-[state=indeterminate]:animate-in",
        "data-[state=checked]:fade-in-0 data-[state=indeterminate]:fade-in-0",
        "data-[state=checked]:zoom-in-95 data-[state=indeterminate]:zoom-in-95",
        "data-[state=checked]:duration-150 data-[state=indeterminate]:duration-150",
      )}
    >
      {props.checked === "indeterminate" ? (
        <Minus className="h-3 w-3" strokeWidth={3} />
      ) : (
        <Icon className="h-3 w-3" strokeWidth={3} />
      )}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = "Checkbox";
